import { Injectable, signal, inject } from '@angular/core';
import { ConsentApiService } from '../shared/services/api/consent-api.service';
import { SelectedWorkspaceStore } from '../stores/selected-workspace.store';
import { catchError, of, tap } from 'rxjs';

export interface CookieConsent {
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
  version: string;
}

const STORAGE_KEY = 'cookieConsent';
const CURRENT_VERSION = '2025-07-17';

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private consentApi = inject(ConsentApiService);
  private workspaceStore = inject(SelectedWorkspaceStore);

  // null = not answered, object = answered
  consent = signal<CookieConsent | null>(null);
  forceShow = signal(false);

  constructor() {
    this.load();
  }

  private load() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const parsed: CookieConsent = JSON.parse(raw);
      if (parsed && parsed.version === CURRENT_VERSION) {
        this.consent.set(parsed);
      }
    } catch {}
  }

  private save(consent: CookieConsent) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    this.consent.set(consent);
    this.forceShow.set(false);
  }

  public giveAnalyticsConsent() {
    const consent: CookieConsent = {
      analytics: true,
      marketing: false,
      timestamp: Date.now(),
      version: CURRENT_VERSION,
    };
    
    // Сохраняем в localStorage сразу для UI
    this.save(consent);

    // Отправляем на сервер
    this.consentApi.submitConsent('ANALYTICS_COOKIES', '1')
      .pipe(
        tap(() => {
          // После успешной отправки обновляем профиль пользователя
          this.workspaceStore.refreshUser();
        }),
        catchError((err) => {
          console.error('Failed to submit analytics consent:', err);
          // Даже если API вернул ошибку, согласие остается в localStorage
          return of(null);
        })
      )
      .subscribe();
  }

  public rejectAnalyticsConsent() {
    const consent: CookieConsent = {
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
      version: CURRENT_VERSION,
    };
    
    // Сохраняем в localStorage
    this.save(consent);
    
    // Отправляем на сервер отказ (value: "0")
    this.consentApi.submitConsent('ANALYTICS_COOKIES', '0')
      .pipe(
        tap(() => {
          this.workspaceStore.refreshUser();
        }),
        catchError((err) => {
          console.error('Failed to submit analytics rejection:', err);
          return of(null);
        })
      )
      .subscribe();
  }

  public openConsentManager() {
    this.forceShow.set(true);
  }

  public hasConsent() {
    return this.consent() !== null;
  }

  public analyticsAllowed() {
    return this.consent()?.analytics ?? false;
  }

  /**
   * Проверяет, нужно ли показывать баннер для незалогиненных пользователей
   * @returns true если баннер нужно показать
   */
  public shouldShowBannerForGuest(): boolean {
    // Если есть согласие в localStorage - не показываем
    if (this.hasConsent()) {
      return false;
    }

    // Если принудительно показываем - показываем
    if (this.forceShow()) {
      return true;
    }

    // Показываем баннер
    return true;
  }

  /**
   * Проверяет, нужно ли показывать баннер на основе данных из профиля пользователя
   * @param userConsents - объект согласий из профиля пользователя
   * @returns true если баннер нужно показать
   */
  public shouldShowBanner(userConsents: Record<string, string> | undefined): boolean {
    // Если согласие есть в профиле (значение "1" означает согласие) - не показываем
    if (userConsents && userConsents['ANALYTICS_COOKIES'] === '1') {
      return false;
    }

    // Если есть согласие в localStorage - не показываем
    if (this.hasConsent()) {
      return false;
    }

    // Если принудительно показываем - показываем
    if (this.forceShow()) {
      return true;
    }

    // Показываем баннер
    return true;
  }
} 